import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { userService } from '../services/userService';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedinUrl: '',
    personalWebsite: '',
    location: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await userService.getProfile();
        if (profileData) {
          setProfile({
            fullName: profileData.fullName || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            linkedinUrl: profileData.linkedinUrl || '',
            personalWebsite: profileData.personalWebsite || '',
            location: profileData.location || ''
          });
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (field) => (event) => {
    setProfile({
      ...profile,
      [field]: event.target.value
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      // Check if profile exists by trying to get it first
      const existingProfile = await userService.getProfile();
      
      if (existingProfile) {
        // Update existing profile
        await userService.updateProfile(profile);
      } else {
        console.error('Profile does not exist');
        // reroute to home page
        navigate('/');
        return;
      }
      
      navigate('/profile');
    } catch (error) {
      console.error('Failed to save profile:', error);
      setError('Failed to save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        sx={{ mb: 3 }}
      >
        Back to Profile
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        {profile.fullName ? 'Edit Profile' : 'Create Profile'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Full Name"
              value={profile.fullName}
              onChange={handleInputChange('fullName')}
              required
              fullWidth
            />

            <TextField
              label="Email"
              type="email"
              value={profile.email}
              onChange={handleInputChange('email')}
              required
              fullWidth
            />

            <TextField
              label="Phone"
              value={profile.phone}
              onChange={handleInputChange('phone')}
              fullWidth
            />

            <TextField
              label="Location"
              value={profile.location}
              onChange={handleInputChange('location')}
              fullWidth
            />

            <TextField
              label="LinkedIn URL"
              type="url"
              value={profile.linkedinUrl}
              onChange={handleInputChange('linkedinUrl')}
              fullWidth
              helperText="e.g., https://linkedin.com/in/yourprofile"
            />

            <TextField
              label="Personal Website"
              type="url"
              value={profile.personalWebsite}
              onChange={handleInputChange('personalWebsite')}
              fullWidth
              helperText="e.g., https://yourwebsite.com"
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfileEdit; 